import { downloadCsv } from 'utils/downloadCsv';
import {
  getAssetIssuesReport,
  getAssignments,
  getAdHocIssues,
  AssignmentRow,
  AdHocIssue,
  ParamsGetAssetIssueReports,
  ParamsGetAssignments,
  ParamsGetAdHocIssues
} from 'services/report';
import { Report } from 'types/report';
import dayjs from 'dayjs';
import { formatAppDate, formatAppDateTime } from 'utils/dateFormat';
import {
  CriticalityLevel,
  getCriticalityDisplay,
  DEFAULT_CRITICALITY_LEVELS
} from 'utils/issueCriticality';

export const ISSUES_EXPORT_PAGE_SIZE = 10000;

const criticalityLabel = (key: string | null | undefined, levels: CriticalityLevel[]) =>
  getCriticalityDisplay(key, levels)?.label ?? key ?? '';

export const exportAssetIssuesCsv = async (
  params: Partial<ParamsGetAssetIssueReports>,
  levels: CriticalityLevel[] = DEFAULT_CRITICALITY_LEVELS
) => {
  const { reports } = await getAssetIssuesReport({
    ...params,
    page: 1,
    size: ISSUES_EXPORT_PAGE_SIZE
  });
  const headers = [
    { key: 'clientName', label: 'Client Name' },
    { key: 'location', label: 'Location' },
    { key: 'equipmentId', label: 'Equipment ID' },
    { key: 'type', label: 'Issue Type' },
    { key: 'description', label: 'Description' },
    { key: 'assignedBy', label: 'Assigned By' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'criticality', label: 'Criticality' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Date Created' }
  ];
  const rows = (reports ?? []).map((issue: Report) => ({
    clientName: issue.room?.client?.clientName ?? '',
    location: issue.room?.name ?? '',
    equipmentId: issue.equipmentId ?? '',
    type: issue.type ?? '',
    description: issue.description ?? '',
    assignedBy: issue.assignedByUser?.name ?? '',
    assignedTo: issue.assignedToUser?.name ?? issue.actionBy?.replace('other_', '') ?? '',
    dueDate:
      issue.dueDate || issue.actionByDate
        ? dayjs(issue.dueDate || issue.actionByDate).format('YYYY-MM-DD')
        : '',
    criticality: issue.criticality?.label ?? criticalityLabel(issue.criticalityKey, levels),
    status: issue.status ?? '',
    createdAt: issue.createdAt ? dayjs(issue.createdAt).format('YYYY-MM-DD HH:mm') : ''
  }));
  downloadCsv('asset-issues', headers, rows);
};

export const exportAssignmentsCsv = async (
  params: Partial<ParamsGetAssignments>,
  levels: CriticalityLevel[] = DEFAULT_CRITICALITY_LEVELS
) => {
  const { assignments } = await getAssignments({
    ...params,
    page: 1,
    size: ISSUES_EXPORT_PAGE_SIZE
  });
  const headers = [
    { key: 'issueType', label: 'Issue Type' },
    { key: 'clientName', label: 'Client Name' },
    { key: 'location', label: 'Location' },
    { key: 'description', label: 'Description' },
    { key: 'assignedBy', label: 'Assigned By' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'criticality', label: 'Criticality' },
    { key: 'status', label: 'Status' }
  ];
  const rows = (assignments ?? []).map((item: AssignmentRow) => ({
    issueType: item.issueType === 'Room' ? 'Location' : (item.issueType ?? ''),
    clientName: item.clientName ?? '',
    location: item.locationPathString || item.location || '',
    description: item.description ?? '',
    assignedBy: item.assignedBy ?? '',
    assignedTo: item.assignedTo ?? '',
    dueDate: item.actionByDate || item.dueDate || '',
    criticality: item.criticalityLabel ?? criticalityLabel(item.criticalityKey, levels),
    status: item.status ?? ''
  }));
  downloadCsv('assignments', headers, rows);
};

export const exportAdHocIssuesCsv = async (
  params: Partial<ParamsGetAdHocIssues>,
  levels: CriticalityLevel[] = DEFAULT_CRITICALITY_LEVELS
) => {
  const { issues } = await getAdHocIssues({
    ...params,
    page: 1,
    size: ISSUES_EXPORT_PAGE_SIZE
  });
  const headers = [
    { key: 'clientName', label: 'Client Name' },
    { key: 'location', label: 'Location Path' },
    { key: 'name', label: 'Issue Name' },
    { key: 'description', label: 'Description' },
    { key: 'reportedBy', label: 'Reported By' },
    { key: 'reportedAt', label: 'Reported At' },
    { key: 'assignedBy', label: 'Assigned By' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'criticality', label: 'Criticality' },
    { key: 'status', label: 'Status' }
  ];
  const rows = (issues ?? []).map((issue: AdHocIssue) => ({
    clientName: issue.client?.clientName ?? '',
    location: issue.locationPathString ?? '',
    name: issue.name || `Adhoc Issue #${issue.id}`,
    description: issue.description ?? '',
    reportedBy: issue.reportedByUser?.name ?? '',
    reportedAt: issue.createdAt ? formatAppDateTime(issue.createdAt) : '',
    assignedBy: issue.assignedByUser?.name ?? '',
    assignedTo: issue.assignedToUser?.name ?? '',
    dueDate: issue.dueDate ? formatAppDate(issue.dueDate) : '',
    criticality: issue.criticality?.label ?? criticalityLabel(issue.criticalityKey, levels),
    status: issue.status ?? ''
  }));
  const prefix = params.archived ? 'ad-hoc-issues-archived' : 'ad-hoc-issues';
  downloadCsv(prefix, headers, rows);
};
